import React, { useState } from 'react';
import { createUser } from '../api/userService';
import './AddUser.css';

const AddUser = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    website: '',
    password: '',
    role: 'user',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');

  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setGeneratedPassword(password);
    setFormData((prev) => ({ ...prev, password }));
    setShowPassword(true);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    // Validation - WordPress style
    if (!formData.username || !formData.username.trim()) {
      setError('Username is required.');
      setLoading(false);
      return;
    }

    if (!formData.email || !formData.email.trim()) {
      setError('Email is required.');
      setLoading(false);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    // Password validation - WordPress allows empty password (auto-generated) or at least 6 characters
    if (formData.password && formData.password.trim() !== '' && formData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      setLoading(false);
      return;
    }

    try {
      const response = await createUser(formData);
      setMessage('User created successfully!');
      
      // Reset form
      setFormData({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        website: '',
        password: '',
        role: 'user',
      });
      setGeneratedPassword('');
      setShowPassword(false);

      // Optionally navigate to All Users tab
      setTimeout(() => {
        window.location.hash = 'all-users';
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error('Failed to create user:', err);
      setError(err.response?.data?.message || 'Failed to create user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    { value: 'user', label: 'Viewer' },
    { value: 'technician', label: 'Developer' },
    { value: 'manager', label: 'Manager' },
    { value: 'admin', label: 'Administrator' },
  ];

  return (
    <div className="wp-add-user-container">
      <h1 className="wp-page-title">Add User</h1>
      <p className="wp-description">Create a brand new user and add them to this site.</p>

      {error && (
        <div className="wp-notice wp-notice-error">
          <p>{error}</p>
        </div>
      )}

      {message && (
        <div className="wp-notice wp-notice-success">
          <p>{message}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <table className="wp-form-table">
          <tbody>
            <tr>
              <th scope="row">
                <label htmlFor="username">
                  Username <span className="wp-required">(required)</span>
                </label>
              </th>
              <td>
                <input
                  type="text"
                  id="username"
                  className="wp-form-input"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  required
                  autoComplete="off"
                />
              </td>
            </tr>
            <tr>
              <th scope="row">
                <label htmlFor="email">
                  Email <span className="wp-required">(required)</span>
                </label>
              </th>
              <td>
                <input
                  type="email"
                  id="email"
                  className="wp-form-input"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                  autoComplete="off"
                />
              </td>
            </tr>
            <tr>
              <th scope="row">
                <label htmlFor="firstName">First Name</label>
              </th>
              <td>
                <input
                  type="text"
                  id="firstName"
                  className="wp-form-input"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  autoComplete="off"
                />
              </td>
            </tr>
            <tr>
              <th scope="row">
                <label htmlFor="lastName">Last Name</label>
              </th>
              <td>
                <input
                  type="text"
                  id="lastName"
                  className="wp-form-input"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  autoComplete="off"
                />
              </td>
            </tr>
            <tr>
              <th scope="row">
                <label htmlFor="website">Website</label>
              </th>
              <td>
                <input
                  type="url"
                  id="website"
                  className="wp-form-input"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="http://"
                  autoComplete="off"
                />
              </td>
            </tr>
            <tr>
              <th scope="row">
                <label htmlFor="password">Password</label>
              </th>
              <td>
                <button
                  type="button"
                  className="wp-button-secondary"
                  onClick={generatePassword}
                  style={{ marginBottom: '10px' }}
                >
                  Generate password
                </button>
                {showPassword && (
                  <div style={{ marginTop: '10px' }}>
                    <input
                      type="text"
                      id="password"
                      className="wp-form-input"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      autoComplete="new-password"
                      style={{ fontFamily: 'monospace', fontSize: '13px' }}
                    />
                    <p className="wp-info-text">
                      Make sure to save this password in a secure location. You will not be able to retrieve it.
                    </p>
                  </div>
                )}
                {!showPassword && (
                  <p className="wp-info-text">
                    A password will be automatically generated for you if you leave this blank. The password must be at least 6 characters long.
                  </p>
                )}
              </td>
            </tr>
            <tr>
              <th scope="row">
                <label htmlFor="role">Role</label>
              </th>
              <td>
                <select
                  id="role"
                  className="wp-form-select"
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          </tbody>
        </table>

        <p className="submit">
          <button type="submit" className="wp-button-primary" disabled={loading}>
            {loading ? 'Adding User...' : 'Add User'}
          </button>
        </p>
      </form>
    </div>
  );
};

export default AddUser;

